import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import {
  useGetOnboardingModulesAPI,
  useGetOnboardingProgressAPI,
  useUpdateOnboardingProgressAPI
} from "@/api/onboarding/useOnboardingAPI";
import { RootState } from "@/store";
import { setActiveStepTourId } from "@/store/slice/onboardingSlice";
import type {
  OnboardingProgress,
  OnboardingProgressStatus
} from "@/types/onboarding";

import { getModuleConfig } from "../configs/getModuleConfig";
import type { ModuleConfig } from "../configs/types";

/**
 * Resolves the zero-based step index to resume at for a given module.
 * Returns the last active step index for in-progress modules, or 0 for
 * not-started and completed modules.
 *
 * Exported so useEnterOnboardingMode can use the same logic when looking up the
 * correct seededData entry for a resumed module.
 */
export function resolveStartIndex(
  config: ModuleConfig | null,
  progress: OnboardingProgress | null
): number {
  if (!config || !progress) return 0;
  if (progress.status === "in_progress" && progress.current_step != null) {
    return Math.max(
      0,
      Math.min(progress.current_step - 1, config.steps.length - 1)
    );
  }
  return 0; // not_started and completed both begin at step 1
}

/**
 * Custom hook to manage the state and logic for the tour steps.
 * It handles the current step, navigation between steps, and updating the onboarding progress.
 */
export function useTourStep() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const moduleCode = useSelector(
    (state: RootState) => state.onboarding.moduleCode
  );

  const { data: modules } = useGetOnboardingModulesAPI({
    enabled: Boolean(moduleCode)
  });

  const { data: progressResponse } = useGetOnboardingProgressAPI(undefined, {
    enabled: Boolean(moduleCode)
  });

  const updateProgress = useUpdateOnboardingProgressAPI();

  const moduleId =
    modules?.modules.find((m) => m.code === moduleCode)?.id ?? null;
  const progress =
    progressResponse?.progress.find((p) => p.module_id === moduleId) ?? null;

  const config = getModuleConfig(moduleCode);
  const startIndex = resolveStartIndex(config, progress);

  // Holds null until the user first navigates. Kept null (rather than seeded to
  // startIndex) until module + progress data load so a refresh-resume never
  // commits step 0 before the progress query resolves: moduleCode rehydrates
  // from redux-persist while the query is still in flight, and a premature 0
  // would fire step 0's onActivate (e.g. openDatasetExplorer) before jumping to
  // the saved step.
  const [stepIndexState, setStepIndexState] = useState<number | null>(null);

  // Derive the active index instead of committing it via setState during render
  // (no setState-in-render; this is derived state, not external-system sync):
  // adopt the resolved start index once data is ready, then hand ownership to
  // navigation. `stepIndexState === null` is the one-shot latch — the moment
  // goToNext/goToPrev writes a number, this returns it verbatim and never
  // re-adopts startIndex.
  const stepIndex =
    stepIndexState === null && config && modules && progressResponse
      ? startIndex
      : stepIndexState;

  const currentStep =
    stepIndex === null ? null : (config?.steps[stepIndex] ?? null);
  const nextStep =
    stepIndex === null ? null : (config?.steps[stepIndex + 1] ?? null);
  const currentStepRoute = currentStep
    ? (currentStep.route ?? config?.route ?? "/")
    : null;
  const totalSteps = config?.steps.length ?? 0;
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;

  // Some modules span pages after their initial route has mounted. Navigate as
  // soon as that step becomes active; steps without an explicit route belong to
  // the module route. useTargetElement will wait for the target on the new route
  // to appear.
  useEffect(() => {
    if (!currentStepRoute || location.pathname === currentStepRoute) return;

    navigate(currentStepRoute);
  }, [currentStepRoute, location.pathname, navigate]);

  // Run the active step's onActivate when the step changes — including on a
  // page-refresh resume, where the tour remounts at the saved step and no user
  // navigation (intro "Let's Start" / onNext) ever fires. Without this, steps
  // whose target lives inside a popup (e.g. the Dataset Explorer) would resume
  // with that popup closed, leaving the user with a blocked screen and no
  // target to click. Keyed on the step id so it runs once per step entry; the
  // returned teardown runs when the step is left or the tour unmounts.
  useEffect(() => {
    const teardown = currentStep?.onActivate?.();

    // teardown is undefined for one-way steps (e.g. those that just open a
    // popup) — the call no-ops. When present it cancels an async side effect the
    // step armed (MutationObserver + timeout in selectOnboardingDatasetWhenReady),
    // not the open itself: without it a leaked observer could fire a stale click
    // on a later screen. See TourStep.onActivate in configs/types.ts.
    return () => {
      teardown?.();
    };
    // Re-run only when the active step changes, not on every new currentStep
    // object identity from useTourStep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep?.id]);

  // The active step's onActivate lifecycle (and its teardown) is owned by
  // OnboardingTour — the sole consumer of this hook — keyed on currentStep.id.
  // It is intentionally NOT duplicated here; running it in both places fires
  // every step's side effects (popup opens, MutationObserver + timeout in
  // selectOnboardingDatasetWhenReady) twice per step entry.

  // Expose the active step's tourId so components outside the tour (e.g. sidebar
  // nav items) can react to it.
  useEffect(() => {
    dispatch(setActiveStepTourId(currentStep?.tourId ?? null));

    return () => {
      dispatch(setActiveStepTourId(null));
    };
  }, [dispatch, currentStep?.tourId]);

  // No useCallback on the handlers below — it would buy nothing here:
  // 1. The only consumer (OnboardingTour) wraps these in fresh inline
  //    closures (handleNext/handleSkip/handleFinish) every render, so no
  //    child is memoized on their identity, and none feed an effect dep.
  // 2. `updateProgress` is the raw react-query mutation object, whose ref
  //    changes every render — memoizing writeProgress on it (and goToNext/
  //    skip in turn) would yield a new identity each render regardless.
  // Plain functions keep the same behavior with less ceremony.

  const writeProgress = async (
    status: OnboardingProgressStatus,
    stepOneBased: number
  ) => {
    if (!config || !moduleId) return;

    await updateProgress.mutateAsync({
      moduleId,
      body: {
        current_step: stepOneBased,
        status,
        walkthrough_version: config.version
      }
    });
  };

  const goToNext = async () => {
    if (!config || stepIndex === null) return;

    currentStep?.onNext?.();

    const nextStepIndex = isLast ? stepIndex : stepIndex + 1;

    if (!isLast) setStepIndexState(nextStepIndex);

    await writeProgress(
      isLast ? "completed" : "in_progress",
      nextStepIndex + 1
    );
  };

  const goToPrev = () => {
    // Read the derived stepIndex, not raw stepIndexState: on a resume the user
    // can click Prev before ever clicking Next, when stepIndexState is still
    // null but stepIndex has derived to startIndex. A functional update on the
    // raw state would receive null and no-op. isFirst (also derived) guarantees
    // stepIndex is a non-null number > 0 here.
    if (isFirst || stepIndex === null) return;

    currentStep?.onPrev?.();

    setStepIndexState(stepIndex - 1);
  };

  const skip = async () => {
    if (stepIndex === null) return;

    await writeProgress("in_progress", stepIndex + 1);
  };

  return {
    currentStep,
    nextStep,
    stepIndex,
    totalSteps,
    isFirst,
    isLast,
    goToNext,
    goToPrev,
    skip
  };
}
